<template>
  <div ref="dropdownContainer" class="relative inline-block text-left">
    <div ref="button" class="h-full">
      <SFButton
        :aria-controls="id"
        :aria-expanded="isDropdownListVisible"
        :aria-invalid="hasErrors"
        :aria-label="ariaLabel"
        :class="[
          ...buttonClasses,
          {
            'py-3.5': isLarge,
            'rounded-md': radius == 'md',
            'rounded-10': radius == 'lg',
            'rounded-xl': radius == 'xl',
            '!border-2 !border-status-error !bg-white !text-status-error shadow-none !outline-0 *:!text-status-error hover:!border-status-error focus:!border-status-error':
              hasErrors,
          },
        ]"
        :disabled="disabled"
        aria-haspopup="true"
        class="group inline-flex size-full justify-between gap-0 rounded-md border border-gray-300 !px-3.5 !py-2 font-semibold leading-5 hover:bg-white"
        size="sm"
        variant="raw"
        @click.prevent="isDropdownListVisible = !isDropdownListVisible"
      >
        <slot name="default">
          <span class="max-w-[80%] text-ellipsis">{{ modelValue }}</span>
        </slot>
        <template #append-icon>
          <IconSnipesArrowsmalldown class="size-4 font-semibold" />
        </template>
      </SFButton>
    </div>
    <Transition
      enter-active-class="transition-all duration-200"
      enter-from-class="translate-y-full md:-translate-y-10 opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition-all duration-200"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="translate-y-full md:-translate-y-10 opacity-0"
    >
      <div
        v-show="isDropdownListVisible"
        :id="id"
        ref="options"
        v-popover="isDropdownListVisible"
        :style="itemsContainerStyle"
        class="absolute m-0 w-full rounded-md bg-white p-2 shadow-secondary ring-1 ring-gray-300 focus:outline-none max-md:backdrop:bg-primary/50"
        :class="[
          {
            'rounded-t-md md:rounded-md': radius == 'md',
            'rounded-t-10 md:rounded-10': radius == 'lg',
            'rounded-t-xl md:rounded-xl': radius == 'xl',
            'block-scrolling': isDropdownListVisible && isMobile,
          },
        ]"
      >
        <ul
          :class="{
            'rounded-md': radius == 'md',
            'rounded-10': radius == 'lg',
            'rounded-xl': radius == 'xl',
          }"
          class="max-h-[330px] overflow-y-auto bg-white p-2 scrollbar-hide md:p-px"
        >
          <li v-for="item in items" :key="`${item}`">
            <slot name="item" v-bind="{ item, selectItem }">
              <div
                :class="{
                  'rounded-md': radius == 'md',
                  'rounded-10': radius == 'lg',
                  'rounded-xl': radius == 'xl',
                }"
                class="flex w-full cursor-pointer items-center justify-between space-x-2 p-2 transition-all hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-accent"
                tabindex="0"
                @click="selectItem(item)"
                @keydown.enter="selectItem(item)"
                @keydown.space.prevent="selectItem(item)"
              >
                {{ item }}
              </div>
            </slot>
          </li>
        </ul>
      </div>
    </Transition>
  </div>
</template>

<script generic="T" lang="ts" setup>
import { computed, ref, useTemplateRef, watch } from 'vue'
import { useEventListener } from '@vueuse/core'
import { vPopover } from '../../directives/popover'
import { useDefaultBreakpoints } from '#storefront-ui/composables'
import { SFButton } from '#storefront-ui/components'
import { useDropdownKeyboardBehavior } from '#storefront-ui'

const {
  isLarge = false,
  disabled = false,
  hasErrors = false,
  radius = 'md',
  buttonClass,
} = defineProps<{
  items: NonNullable<T>[]
  buttonClass?: string[] | string
  id: string
  hasErrors?: boolean
  isLarge?: boolean
  disabled?: boolean
  ariaLabel?: string
  radius?: 'md' | 'lg' | 'xl'
}>()

const isDropdownListVisible = defineModel<boolean>('visible', {
  default: false,
})

const modelValue = defineModel<T>('modelValue')

const buttonClasses = computed(() => {
  return Array.isArray(buttonClass) ? buttonClass : [buttonClass]
})

const selectItem = (item: T) => {
  modelValue.value = item
  isDropdownListVisible.value = false
}

const dropdownContainer = ref()

const button = ref()
const itemsContainerStyle = ref()
const { smaller } = useDefaultBreakpoints()
const isMobile = smaller('md')

const buttonRef = useTemplateRef('button')
const rootRef = useTemplateRef('dropdownContainer')
const optionsRef = useTemplateRef('options')
useDropdownKeyboardBehavior(
  { rootRef, buttonRef, optionsRef },
  {
    isOpen: isDropdownListVisible,
    close: () => (isDropdownListVisible.value = false),
    open: () => (isDropdownListVisible.value = true),
  },
)

const calculateDropdown = () => {
  if (!button.value) {
    return
  }

  if (isMobile.value) {
    itemsContainerStyle.value = {
      left: `0`,
      bottom: `0`,
      top: 'auto',
      width: `100%`,
      position: 'fixed',
    }
    return
  }

  const rect = button.value.getBoundingClientRect()
  itemsContainerStyle.value = {
    left: `${rect.left + window.scrollX}px`,
    top: `${rect.top + window.scrollY}px`,
    width: `${button.value!.offsetWidth}px`,
  }
}

let cleanupResizeListener: ReturnType<typeof useEventListener> | undefined
watch(isDropdownListVisible, (isOpen) => {
  if (isOpen) {
    calculateDropdown()
    cleanupResizeListener = useEventListener('resize', calculateDropdown)
    return
  }

  cleanupResizeListener?.()
  cleanupResizeListener = undefined
})
</script>
